import {useMemo, useState, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {CurrencyValue} from '../../../common/currencyValue';
import Checkbox from '../../../common/simple/Checkbox';
import {constants} from '../../../common/constants/constants';
import checkedPayments from '../../../images/newDesign/checkedPayments.svg';
import arrow from '../../../images/newDesign/arrow_black.svg';
import Button from '../../../common/Button';
import {StyledSpecialCard} from './SpecialPaymentCard.styled';
import SpecialPaymentItem from './SpecialPaymentItem';
import {useDispatch, useSelector} from "react-redux";
import {selectOneTimePayment, setOneTimeSelectedPayments} from "../../../store/tenant/slice";
import { paymentsCardType } from './PaymentPageLogic';


export function SpecialPaymentCard({ onMarkAsPaid = () => void undefined }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const ref = useRef(null);
    const btnRef = useRef(null);

    const specialPayments = useSelector(state => state.tenant.allPayments?.one_time_payments ?? []);
    const selectedSpecialPayments = useSelector(state => state.tenant.oneTimeSelectedPayments);
    const buildingMarkAsPaidDisabled = useSelector(state => state.tenant.buildingMarkAsPaidDisabled);

    const specialPaymentItems = useMemo(
        () => specialPayments.map(payment => ({ payment, isChecked: selectedSpecialPayments.some(p => p.uid === payment.uid) })),
        [specialPayments, selectedSpecialPayments]
    );
    const currentYear = (new Date()).getFullYear();
    const currency = useMemo(() => specialPayments[0]?.amount_left?.currency ?? constants.defaultCurrency, [specialPayments]);
    const currentYearSpecialPayments = useMemo(() => specialPayments.filter(p => (new Date(p.createdAt)).getFullYear() === currentYear), [specialPayments]);
    const pastYearsSpecialPayments = useMemo(() => specialPayments.filter(p => (new Date(p.createdAt)).getFullYear() < currentYear), [specialPayments]);
    const currentYearAmount = useMemo(() => currentYearSpecialPayments.reduce((acc, p) => acc + p.amount_left.value, 0), [currentYearSpecialPayments]);
    const pastYearsAmount = useMemo(() => pastYearsSpecialPayments.reduce((acc, p) => acc + p.amount_left.value, 0), [pastYearsSpecialPayments]);
    const allCheckboxChecked = useMemo(() => specialPayments.length === selectedSpecialPayments.length, [specialPayments, selectedSpecialPayments]);

    const [infoOpened, setInfoOpened] = useState(false);

    function handleCheckboxChange(paymentItem) {
        dispatch(selectOneTimePayment(paymentItem));
    }

    // checkbox to check or uncheck all one time payments and set selectedPaymentsList either to an empty array or the whole payments array
    function handleAllCheckboxChange(checked) {
        dispatch(setOneTimeSelectedPayments(checked ? specialPayments : []));
    }

    function SpecialPaymentList() {
        return specialPaymentItems.map((item, i) => (
            <SpecialPaymentItem
                key={i}
                paymentItem={item}
                onChange={handleCheckboxChange}
            />
        ));
    }

    const handleClickOutsideDropdown = e => {
        if (ref.current && !ref.current.contains(e.target)) setInfoOpened(false);
        else if (btnRef.current && btnRef.current.contains(e.target)) setInfoOpened(true);
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideDropdown, true);
    }, []);

    const closeDropdown = () => setInfoOpened(false);

    return (
        <StyledSpecialCard opened={infoOpened}>
            <div className='card-title border-bottom'>
                <img src={checkedPayments} />
                {t('payments.special_payment')}
            </div>

            <div className='payment border-bottom'>
                {t('payments.debt_amount_past')}
                <CurrencyValue notInCents={true} currency={currency}>{pastYearsAmount}</CurrencyValue>
            </div>

            <div className='payment border-bottom'>
                {t('payments.current_year_amount')}
                <CurrencyValue notInCents={true} currency={currency}>{currentYearAmount}</CurrencyValue>                          
            </div>

            <div className='payment'>
                {t('payments.selected_payments')}
                <div className='value'>{selectedSpecialPayments.length}</div>
            </div>

            <div className='dropdown' ref={btnRef}>
                {t('payments.special_details')}
                <img src={arrow} className='dropdown-arrow' />
            </div>

            {infoOpened && (
                <div className='info' ref={ref}>
                    <Checkbox
                        checked={allCheckboxChecked}
                        onChange={handleAllCheckboxChange}
                        wrapperClassName='check-all'
                    />
                    <SpecialPaymentList />

                    {!buildingMarkAsPaidDisabled && (
                        <div onClick={onMarkAsPaid} className='mark-as-paid'>{t('payments.mark_as_paid')}</div>
                    )}

                    <Button outlined onClick={closeDropdown}>{t('payments.apply')}</Button>
                </div>
            )}
        </StyledSpecialCard>
    )
}
